import { createSignal, onCleanup, onMount, Show, type Component } from 'solid-js';
import { Portal } from 'solid-js/web';
import { persistantStore } from '@store/persistantStore';
import { shouldShowCapitanFloatingButton, probeAssistantHealth } from '@utils/assistantAvailability';
import { createCapitanSplitPanel } from './createCapitanSplitPanel';
import { debug } from '@utils/console';
import '@styles/Assistant.css';

export type CapitanFloatingButtonProps = {
  openInSplitView: (
    componentToLoad: () => unknown,
    title: string,
    menuName?: string | null,
    leftComponentFactory?: (() => unknown) | null,
    options?: { rightPanelWidthPercent?: number }
  ) => void;
  isSplitView: () => boolean;
  rightPanelMenu: () => string | null;
};

const CapitanFloatingButton: Component<CapitanFloatingButtonProps> = (props) => {
  const [visible, setVisible] = createSignal(false);
  const [hoverMain, setHoverMain] = createSignal(false);
  let hideTimer: ReturnType<typeof setTimeout> | null = null;

  const refresh = async () => {
    const show = await shouldShowCapitanFloatingButton({
      menu: persistantStore.selectedMenu(),
      rightPanelMenu: props.rightPanelMenu(),
      isSplitView: props.isSplitView(),
    });
    setVisible(show);
  };

  onMount(() => {
    void refresh();
    const interval = setInterval(() => void probeAssistantHealth().then(() => void refresh()), 60_000);
    const onMove = (e: MouseEvent) => {
      const main = document.getElementById('main-content');
      const host = document.querySelector('.capitan-floating-host');
      const t = e.target as Node | null;
      const over = !!(main && t && (main.contains(t) || host?.contains(t)));
      setHoverMain(over);
      if (over && hideTimer) {
        clearTimeout(hideTimer);
        hideTimer = null;
      } else if (!over) {
        hideTimer = setTimeout(() => setHoverMain(false), 500);
      }
    };
    document.addEventListener('mousemove', onMove);
    onCleanup(() => {
      clearInterval(interval);
      document.removeEventListener('mousemove', onMove);
      if (hideTimer) clearTimeout(hideTimer);
    });
  });

  // Re-check when menu changes
  const menuCheck = setInterval(() => void refresh(), 2000);
  onCleanup(() => clearInterval(menuCheck));

  const open = () => {
    const launch = {
      page_name: persistantStore.selectedPage() || persistantStore.selectedMenu(),
      menu_name: persistantStore.selectedMenu(),
      project_id: Number(persistantStore.selectedProjectId()) || 0,
    };
    debug('[Capitan] open split panel', launch);
    props.openInSplitView(
      createCapitanSplitPanel(launch),
      'Capitan',
      'ASSISTANT',
      null,
      { rightPanelWidthPercent: 25 }
    );
  };

  return (
    <Show when={visible()}>
      <Portal>
        <div
          class={`capitan-floating-host ${hoverMain() ? 'is-visible' : ''}`}
          onMouseEnter={() => setHoverMain(true)}
        >
          <button type="button" class="capitan-floating-button" onClick={open} title="Ask Capitan">
            Capitan
          </button>
        </div>
      </Portal>
    </Show>
  );
};

export default CapitanFloatingButton;
